/**
 * Graph Colors and Styling Configuration for Case Data Type Analytics
 * 
 * This file contains all the color definitions and styling configurations
 * used throughout the case data type analytics charts and components.
 * 
 * Color scheme follows Material Design guidelines and accessibility standards.
 */

// Primary brand colors with transparency
export const primaryColors = {
  blue: 'rgba(54, 162, 235, 0.8)',
  red: 'rgba(255, 99, 132, 0.8)',
  green: 'rgba(75, 192, 192, 0.8)',
  yellow: 'rgba(255, 206, 86, 0.8)',
  purple: 'rgba(153, 102, 255, 0.8)',
  orange: 'rgba(255, 159, 64, 0.8)',
  grey: 'rgba(199, 199, 199, 0.8)',
  teal: 'rgba(54, 205, 194, 0.8)',
  indigo: 'rgba(139, 69, 255, 0.8)'
};

// Border colors (fully opaque versions)
export const borderColors = {
  blue: 'rgba(54, 162, 235, 1)',
  red: 'rgba(255, 99, 132, 1)',
  green: 'rgba(75, 192, 192, 1)',
  yellow: 'rgba(255, 206, 86, 1)',
  purple: 'rgba(153, 102, 255, 1)',
  orange: 'rgba(255, 159, 64, 1)',
  grey: 'rgba(199, 199, 199, 1)',
  teal: 'rgba(54, 205, 194, 1)',
  indigo: 'rgba(139, 69, 255, 1)'
};

// Metric-specific color mappings for case data type analytics
export const metricColors = {
  allocated: {
    background: primaryColors.blue,
    border: borderColors.blue,
    hex: '#36A2EB'
  },
  attempted: {
    background: primaryColors.red,
    border: borderColors.red,
    hex: '#FF6384'
  },
  connected: {
    background: primaryColors.green,
    border: borderColors.green,
    hex: '#4BC0C0'
  },
  unreached: {
    background: primaryColors.yellow,
    border: borderColors.yellow,
    hex: '#FFCE56'
  },
  childAboveTwo: {
    background: primaryColors.purple,
    border: borderColors.purple,
    hex: '#9966FF'
  },
  notInPortal: {
    background: primaryColors.orange,
    border: borderColors.orange,
    hex: '#FF9F40'
  },
  pending: {
    background: primaryColors.grey,
    border: borderColors.grey,
    hex: '#C7C7C7'
  },
  active: {
    background: primaryColors.teal,
    border: borderColors.teal,
    hex: '#36CDC2'
  },
  unattempted: {
    background: primaryColors.indigo,
    border: borderColors.indigo,
    hex: '#8B45FF'
  }
};

// Metrics configuration with consistent styling
export const metricsConfig = [
  {
    key: 'allocated',
    label: 'Allocated',
    color: metricColors.allocated.hex,
    backgroundColor: metricColors.allocated.background,
    borderColor: metricColors.allocated.border,
    description: 'Total cases allocated to agents'
  },
  {
    key: 'attempted',
    label: 'Attempted',
    color: metricColors.attempted.hex,
    backgroundColor: metricColors.attempted.background,
    borderColor: metricColors.attempted.border,
    description: 'Cases that have been attempted by agents'
  },
  {
    key: 'connected',
    label: 'Connected',
    color: metricColors.connected.hex,
    backgroundColor: metricColors.connected.background,
    borderColor: metricColors.connected.border,
    description: 'Successfully connected cases'
  },
  {
    key: 'unreached',
    label: 'Unreached',
    color: metricColors.unreached.hex,
    backgroundColor: metricColors.unreached.background,
    borderColor: metricColors.unreached.border,
    description: 'Cases that could not be reached'
  },
  {
    key: 'childAboveTwo',
    label: 'Child above 2 Years',
    color: metricColors.childAboveTwo.hex,
    backgroundColor: metricColors.childAboveTwo.background,
    borderColor: metricColors.childAboveTwo.border,
    description: 'Cases with children above 2 years of age'
  },
  {
    key: 'notInPortal',
    label: 'Not in the portal',
    color: metricColors.notInPortal.hex,
    backgroundColor: metricColors.notInPortal.background,
    borderColor: metricColors.notInPortal.border,
    description: 'Cases not available in the portal'
  },
  {
    key: 'pending',
    label: 'Pending',
    color: metricColors.pending.hex,
    backgroundColor: metricColors.pending.background,
    borderColor: metricColors.pending.border,
    description: 'Cases waiting for action'
  },
  {
    key: 'active',
    label: 'Active',
    color: metricColors.active.hex,
    backgroundColor: metricColors.active.background,
    borderColor: metricColors.active.border,
    description: 'Currently active cases'
  },
  {
    key: 'unattempted',
    label: 'Unattempted',
    color: metricColors.unattempted.hex,
    backgroundColor: metricColors.unattempted.background,
    borderColor: metricColors.unattempted.border,
    description: 'Cases that have not been attempted yet'
  }
];

// Chart styling configurations
export const chartStyles = {
  // Default chart padding
  padding: {
    top: 20,
    right: 30,
    bottom: 20,
    left: 10
  },

  // Grid line styling
  grid: {
    color: 'rgba(0, 0, 0, 0.1)',
    lineWidth: 1,
    drawBorder: false
  },

  // Font configurations
  fonts: {
    title: {
      size: 16,
      weight: 'bold',
      family: 'Roboto, Arial, sans-serif'
    },
    labels: {
      size: 12,
      weight: 'normal',
      family: 'Roboto, Arial, sans-serif'
    },
    ticks: {
      size: 11,
      weight: 'normal',
      family: 'Roboto, Arial, sans-serif'
    }
  },

  // Tooltip styling
  tooltip: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    titleColor: '#fff',
    bodyColor: '#fff',
    borderColor: '#ddd',
    borderWidth: 1,
    cornerRadius: 6,
    displayColors: true
  },

  // Legend styling
  legend: {
    position: 'top',
    align: 'center',
    labels: {
      boxWidth: 12,
      padding: 20,
      usePointStyle: false
    }
  },

  // Animation settings
  animation: {
    duration: 750,
    easing: 'easeInOutQuart'
  }
};

// Data type specific styling
export const dataTypeStyles = {
  All: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderColor: '#667eea',
    accentColor: '#667eea'
  },
  One: {
    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    borderColor: '#f093fb',
    accentColor: '#f093fb'
  },
  Two: {
    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    borderColor: '#4facfe',
    accentColor: '#4facfe'
  }
};

// Utility functions for color manipulation
export const colorUtils = {
  /**
   * Convert hex color to rgba with specified opacity
   * @param {string} hex - Hex color code
   * @param {number} opacity - Opacity value (0-1)
   * @returns {string} RGBA color string
   */
  hexToRgba: (hex, opacity = 0.8) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  },

  /**
   * Get color scheme for a specific metric
   * @param {string} metricKey - Key of the metric
   * @returns {object} Color scheme object
   */
  getMetricColors: (metricKey) => {
    return metricColors[metricKey] || metricColors.allocated;
  },

  /**
   * Generate gradient background for charts
   * @param {string} startColor - Starting color
   * @param {string} endColor - Ending color
   * @param {number} angle - Gradient angle in degrees
   * @returns {string} CSS gradient string
   */
  createGradient: (startColor, endColor, angle = 135) => {
    return `linear-gradient(${angle}deg, ${startColor} 0%, ${endColor} 100%)`;
  }
};

// Export arrays for easy iteration
export const metricBackgroundColors = metricsConfig.map(metric => metric.backgroundColor);
export const metricBorderColors = metricsConfig.map(metric => metric.borderColor);
export const metricHexColors = metricsConfig.map(metric => metric.color);
export const metricLabels = metricsConfig.map(metric => metric.label);

// Theme variations
export const themes = {
  light: {
    background: '#ffffff',
    text: '#333333',
    grid: 'rgba(0, 0, 0, 0.1)',
    border: '#e0e0e0'
  },
  dark: {
    background: '#1a1a1a',
    text: '#ffffff',
    grid: 'rgba(255, 255, 255, 0.1)',
    border: '#404040'
  }
};

export default {
  metricColors,
  metricsConfig,
  chartStyles,
  dataTypeStyles,
  colorUtils,
  themes
};
